/**
 * Ticket-domain helpers and label constants — Phase 3.1.a.3.
 *
 * Pulled out of admin.js so view modules (and admin.js itself) can import
 * the formatters/role-derivation helpers directly instead of receiving
 * them via `configure({helpers})` injection.
 *
 * State-dependent helpers (current admin user, technician profile lookup)
 * use the thunk pattern from `lib/roles.js`: admin.js wires the getters
 * once at boot, and the helpers re-evaluate them on every call so live
 * mutation of `ADMIN_ME` / the profile Map propagates without explicit
 * notification.
 */

import { humanizeIdentifier } from './format.js';

// ----- Label constants -------------------------------------------------

export const TICKET_STATUS_LABELS = {
  pending: 'Pending',
  in_progress: 'In progress',
  waiting_customer: 'Waiting customer',
  escalated: 'Escalated',
  resolved_by_technician: 'Resolved by technician',
  parked: 'Parked',
  closed: 'Closed',
};

export const TICKET_PRIORITY_LABELS = {
  p1_low: 'P1 Low',
  p2_medium: 'P2 Medium',
  p3_high: 'P3 High',
  p4_urgent: 'P4 Urgent',
  p5_critical: 'P5 Critical',
};

export const TICKET_ASSIGNEE_LABELS = {
  agent: 'Agent',
  supervisor: 'Supervisor',
  tech_officer: 'Tech officer',
  tech_manager: 'Tech manager',
  billing_officer: 'Billing officer',
  billing_manager: 'Billing manager',
  sales_officer: 'Sales officer',
  sales_manager: 'Sales supervisor',
};

export const SUPERVISOR_ROLE_DISPLAY = {
  customer_care_supervisor: 'customer care supervisor',
  field_tech_supervisor: 'field technician supervisor',
  billing_supervisor: 'billing supervisor',
  sales_supervisor: 'sales supervisor',
};

export const SUPERVISOR_ROLE_NAMES = new Set([
  'customer_care_supervisor',
  'field_tech_supervisor',
  'billing_supervisor',
  'sales_supervisor',
]);

export const ESCALATION_SUPERVISOR_TARGETS = {
  agent: ['customer_care_supervisor'],
  supervisor: ['customer_care_supervisor'],
  tech_officer: ['field_tech_supervisor'],
  tech_manager: ['field_tech_supervisor'],
  billing_officer: ['billing_supervisor'],
  billing_manager: ['billing_supervisor'],
  sales_officer: ['sales_supervisor'],
  sales_manager: ['sales_supervisor'],
};

export const BILLING_COMPLAINT_TYPES = new Set([
  'billing_issue',
  'bill_enquiry',
  'wave_payment_issue',
]);

export const SALES_COMPLAINT_TYPES = new Set(['sales_enquiry']);

export const ASSIGNABLE_ROLE_NAMES = new Set([
  'customer_care',
  'customer_care_supervisor',
  'billing_officer',
  'billing_supervisor',
  'field_tech_supervisor',
]);

export const CUSTOMER_CARE_ASSIGN_ROLES = new Set(['customer_care', 'customer_care_supervisor']);
export const CUSTOMER_CARE_SUPERVISOR_ROLE = 'customer_care_supervisor';
export const FIELD_TECH_ROLE = 'field_tech';
export const FIELD_TECH_SUPERVISOR_ROLE = 'field_tech_supervisor';
export const SYSTEM_ADMIN_ROLE = 'system_admin';

// ----- Ticket form / dialog constants ------------------------------- (Phase 3.2.c)

export const TICKET_PRIORITY_OPTIONS = ['p5_critical', 'p4_urgent', 'p3_high', 'p2_medium', 'p1_low'];

export const TICKET_COMPLAINT_OPTIONS = [
  'no_connection',
  'slow_connection',
  'intermitent_connection',
  'wifi_disappears',
  'wifi_coverage_issue',
  'connected_no_internet',
  'lan_device_issue',
  'replace_router',
  'replace_cables',
  'replace_outdoor',
  'replace_drop_cable',
  'replace_fiber_modem',
  'billing_issue',
  'bill_enquiry',
  'overdue_bill',
  'sales_enquiry',
  'app_issue',
  'hotspot_not_working',
  'wave_payment_issue',
  'move_location',
  'pause_internet',
  'change_wifi_password',
  'device_or_pole_fell',
  'reconnect_internet',
  'installation',
];

export const ENQUIRY_TYPE_OPTIONS = [
  { value: 'service_quality', label: 'Service quality' },
  { value: 'billing', label: 'Billing & payments' },
  { value: 'installation', label: 'Installation & activation' },
  { value: 'account', label: 'Account & profile' },
  { value: 'sales', label: 'Sales & upsell' },
  { value: 'follow_up', label: 'Follow-up / callbacks' },
  { value: 'compliance', label: 'Compliance & policy' },
  { value: 'feedback', label: 'Feedback & complaints' },
  { value: 'other', label: 'Other' },
];

export const CONTACT_METHOD_LABELS = {
  email: 'Email',
  whatsapp: 'WhatsApp',
  phone: 'Phone call',
  app: 'Mobile app',
};

export const CONTACT_METHOD_OPTIONS = ['email', 'whatsapp', 'phone', 'app'];

export const CHANNEL_PREFERRED_CONTACT = {
  email: 'email',
  whatsapp: 'whatsapp',
  pbx: 'phone',
  app: 'app',
};

export const TICKET_VISIBILITY_LABELS = {
  internal: 'Internal',
  customer: 'Customer reply',
};

export const COMMENT_REVIEW_LABELS = {
  pending: 'Pending review',
  accepted: 'Accepted',
  delivered: 'Sent to customer',
  rejected: 'Rejected',
  failed: 'Failed',
  overwritten: 'Overridden',
};

export const COMMENT_EDITABLE_STATES = new Set(['pending', 'failed', 'rejected']);
export const COMMENT_RETRY_STATES = new Set(['failed', 'rejected']);
export const COMMENT_OVERRIDE_STATES = new Set(['failed', 'rejected']);
export const COMMENT_SUPERVISOR_ROLES = new Set([
  'root',
  'customer_care_supervisor',
  'field_tech_supervisor',
  'billing_supervisor',
  'sales_supervisor',
]);

export const TICKET_CLOSE_TEMPLATES = {
  resolved: { body: 'Issue resolved. Please let us know if the problem returns.', visibility: 'customer' },
  no_response: { body: 'Closing ticket after multiple follow-ups with no response. Reach out to re-open.', visibility: 'customer' },
  duplicate: { body: 'Ticket closed as duplicate. Tracking continues under the primary case.', visibility: 'internal' },
};

export const META_ORIGIN_ALIASES = new Map([
  ['mobile_profile', 'Mobile App'],
  ['admin', 'Admin Console'],
  ['technician', 'Technician App'],
  ['channel:whatsapp', 'WhatsApp (Channel Intake)'],
  ['channel:email', 'Email (Channel Intake)'],
  ['channel:pbx', 'PBX Call'],
  ['channel:pbx_call', 'PBX Call'],
  ['channel:app', 'Mobile App'],
  ['channel:sms', 'SMS'],
]);

export const ASSIGN_MODE_ASSIGN = 'assign';
export const ASSIGN_MODE_ESCALATE = 'escalate';

// ----- State injection ------------------------------------------------

let getCurrentUser = () => ({ roles: [] });
let getTechnicianProfile = () => null;

/**
 * Wire the current-admin-user lookup. admin.js calls this once at boot
 * with a thunk that returns the live `ADMIN_ME` object so role refresh
 * after login propagates to userEligibleForTicketAssignment without
 * explicit notification.
 */
export function setCurrentUserGetter(fn) {
  if (typeof fn === 'function') getCurrentUser = fn;
}

/**
 * Wire the technician-profile lookup. admin.js calls this once at boot
 * with a function `(adminUserId) => technicianProfile|null` so
 * deriveAdminDisplayName can resolve technician display names without
 * importing the technicians-view state.
 */
export function setTechnicianProfileLookup(fn) {
  if (typeof fn === 'function') getTechnicianProfile = fn;
}

// ----- Pure formatters -------------------------------------------------

export function formatTicketStatus(status) {
  return TICKET_STATUS_LABELS[String(status || '').toLowerCase()] || (status ? String(status).replace(/_/g, ' ') : '—');
}

export function formatTicketComplaint(complaint) {
  if (!complaint) return '—';
  return String(complaint).replace(/_/g, ' ');
}

export function formatTicketPriority(priority) {
  return TICKET_PRIORITY_LABELS[String(priority || '').toLowerCase()] || (priority ? String(priority).toUpperCase() : '—');
}

export function formatTicketAssignee(role, user) {
  const roleKey = String(role || '').toLowerCase();
  const roleLabel = role
    ? (TICKET_ASSIGNEE_LABELS[roleKey] || String(role).replace(/_/g, ' '))
    : '';
  if (user && (user.username || user.email)) {
    const name = user.username || user.email || `User #${user.id}`;
    return roleLabel ? `${name} (${roleLabel})` : name;
  }
  if (roleLabel) return roleLabel;
  return 'Unassigned';
}

export function formatLaneCollapsedLabel(label) {
  const text = (label == null ? '' : String(label)).trim();
  if (!text) return 'Lane';
  const parts = text.split(/\s+/).filter((part) => part);
  if (parts.length === 1) {
    const single = parts[0];
    if (single.length <= 3) return single.toUpperCase();
    return single.slice(0, 3).toUpperCase();
  }
  const initials = parts.slice(0, 2).map((part) => part[0] || '').join('').toUpperCase();
  return initials || text.slice(0, 3).toUpperCase();
}

export function describeDuration(ms) {
  const abs = Math.abs(ms);
  const minutes = Math.floor(abs / 60000);
  const days = Math.floor(minutes / (60 * 24));
  const hours = Math.floor((minutes % (60 * 24)) / 60);
  const mins = minutes % 60;
  const parts = [];
  if (days) parts.push(`${days}d`);
  if (hours) parts.push(`${hours}h`);
  if (!days && mins) parts.push(`${mins}m`);
  if (!parts.length) parts.push('0m');
  return parts.join(' ');
}

export function formatAiRecommendations(value) {
  if (!value || typeof value !== 'object') return '';
  try {
    return JSON.stringify(value, null, 2);
  } catch (err) {
    console.warn('Unable to stringify AI recommendations', err);
    return String(value);
  }
}

export function resolveTicketDisplay(ticket, fallbackId, fallbackSubject, fallbackShortCode) {
  const trimmedSubject = typeof fallbackSubject === 'string' ? fallbackSubject.trim() : '';
  const subject = ticket && typeof ticket.subject === 'string' && ticket.subject.trim()
    ? ticket.subject.trim()
    : trimmedSubject;
  const ticketMeta = ticket && ticket.meta && typeof ticket.meta === 'object' ? ticket.meta : {};
  const metaShort = ticketMeta && ticketMeta.ticket_short_code ? String(ticketMeta.ticket_short_code).trim() : '';
  const fallbackShort = typeof fallbackShortCode === 'string' ? fallbackShortCode.trim() : '';
  const shortCode = metaShort || fallbackShort;
  const label = subject || (shortCode ? `#${shortCode}` : (fallbackId ? `Ticket ${fallbackId}` : 'Ticket'));
  return { subject, shortCode, label };
}

// ----- Role helpers ----------------------------------------------------

export function extractRoleNames(source) {
  const raw = Array.isArray(source?.roles) ? source.roles : [];
  const names = [];
  raw.forEach((role) => {
    if (typeof role === 'string') {
      names.push(role);
    } else if (role && typeof role === 'object') {
      if (typeof role.name === 'string') {
        names.push(role.name);
      } else if (typeof role.role === 'string') {
        names.push(role.role);
      }
    }
  });
  return names;
}

export function userIsSupervisor(user, restrictSet = null) {
  const roles = extractRoleNames(user);
  const supervisorRoles = roles.filter((role) => SUPERVISOR_ROLE_NAMES.has(role));
  if (!supervisorRoles.length) return false;
  if (restrictSet && restrictSet.size) {
    return supervisorRoles.some((role) => restrictSet.has(role));
  }
  return true;
}

export function derivePrimaryAssignmentRole(user) {
  const roles = extractRoleNames(user);
  const priority = [
    'customer_care_supervisor',
    'customer_care',
    'field_tech_supervisor',
    'field_tech',
    'billing_supervisor',
    'billing_officer',
    'sales_supervisor',
    'sales_officer',
  ];
  const found = priority.find((role) => roles.includes(role));
  if (!found) return null;
  switch (found) {
    case 'customer_care_supervisor':
      return TICKET_ASSIGNEE_LABELS.supervisor;
    case 'customer_care':
      return TICKET_ASSIGNEE_LABELS.agent;
    case 'field_tech_supervisor':
      return TICKET_ASSIGNEE_LABELS.tech_manager;
    case 'field_tech':
      return TICKET_ASSIGNEE_LABELS.tech_officer;
    case 'billing_supervisor':
      return TICKET_ASSIGNEE_LABELS.billing_manager;
    case 'billing_officer':
      return TICKET_ASSIGNEE_LABELS.billing_officer;
    case 'sales_supervisor':
      return TICKET_ASSIGNEE_LABELS.sales_manager;
    case 'sales_officer':
      return TICKET_ASSIGNEE_LABELS.sales_officer;
    default:
      return found.replace(/_/g, ' ');
  }
}

export function describeSupervisorTargets(roleSet) {
  if (!roleSet || roleSet.size === 0) return '';
  const labels = Array.from(roleSet).map((role) => SUPERVISOR_ROLE_DISPLAY[role] || role.replace(/_/g, ' '));
  if (labels.length === 1) return labels[0];
  if (labels.length === 2) return `${labels[0]} or ${labels[1]}`;
  return `${labels.slice(0, -1).join(', ')}, or ${labels[labels.length - 1]}`;
}

export function deriveSupervisorAdminRoles(ticket) {
  const fallback = new Set(['customer_care_supervisor']);
  if (!ticket) return fallback;
  const roleKey = String(ticket.assignee_role || '').toLowerCase();
  if (roleKey && ESCALATION_SUPERVISOR_TARGETS[roleKey]) {
    return new Set(ESCALATION_SUPERVISOR_TARGETS[roleKey]);
  }
  const complaint = String(ticket.complaint_type || '').toLowerCase();
  if (complaint && BILLING_COMPLAINT_TYPES.has(complaint)) {
    return new Set(['billing_supervisor']);
  }
  if (complaint && SALES_COMPLAINT_TYPES.has(complaint)) {
    return new Set(['sales_supervisor']);
  }
  if (roleKey === 'tech_officer' || roleKey === 'tech_manager') {
    return new Set(['field_tech_supervisor']);
  }
  return fallback;
}

/**
 * Decide whether the *current* admin user is allowed to assign a ticket
 * to `user`. Reads the current admin's roles via the thunk wired up by
 * `setCurrentUserGetter`.
 */
export function userEligibleForTicketAssignment(user) {
  if (!user) return false;
  // Disabled accounts can never be assigned new work.
  if (user.is_active === false) return false;
  const roles = extractRoleNames(user);
  if (!roles.length) return false;
  if (roles.includes(SYSTEM_ADMIN_ROLE)) return false;
  const adminMe = getCurrentUser() || {};
  const currentRoles = new Set(Array.isArray(adminMe.roles) ? adminMe.roles : []);
  if (currentRoles.has('root') || currentRoles.has(SYSTEM_ADMIN_ROLE)) {
    return true;
  }
  const targetIsTechSupervisor = roles.includes(FIELD_TECH_SUPERVISOR_ROLE);
  const targetIsTechnician = roles.includes(FIELD_TECH_ROLE);
  const targetIsCareSupervisor = roles.includes(CUSTOMER_CARE_SUPERVISOR_ROLE);
  const targetIsCareAgent = roles.includes('customer_care');
  if (currentRoles.has(FIELD_TECH_SUPERVISOR_ROLE)) {
    return (
      targetIsTechnician
      || targetIsTechSupervisor
      || targetIsCareSupervisor
      || targetIsCareAgent
    );
  }
  if (currentRoles.has(FIELD_TECH_ROLE)) {
    return targetIsTechSupervisor;
  }
  const actorIsCustomerCare = Array.from(CUSTOMER_CARE_ASSIGN_ROLES).some((role) => currentRoles.has(role));
  if (actorIsCustomerCare) {
    if (targetIsTechnician || targetIsTechSupervisor) {
      return targetIsTechSupervisor;
    }
    return roles.some((role) => ASSIGNABLE_ROLE_NAMES.has(role));
  }
  if (targetIsTechnician) {
    return targetIsTechSupervisor;
  }
  return roles.some((role) => ASSIGNABLE_ROLE_NAMES.has(role));
}

// ----- Admin-user formatters ------------------------------------------

export function fallbackAdminContact(user) {
  if (!user) return '';
  return (
    (user.email && user.email.trim())
    || (user.primary_email && user.primary_email.trim())
    || (user.phone && String(user.phone).trim())
    || ''
  );
}

export function deriveAdminDisplayName(user) {
  if (!user) return '';
  const adminId = user.id != null ? Number(user.id) : null;
  if (adminId != null && !Number.isNaN(adminId)) {
    const profile = getTechnicianProfile(adminId);
    if (profile && typeof profile.display_name === 'string' && profile.display_name.trim()) {
      return profile.display_name.trim();
    }
  }
  const direct =
    (user.display_name && user.display_name.trim())
    || (user.full_name && user.full_name.trim());
  if (direct) return direct;
  const username = (user.username && user.username.trim()) || '';
  const usernameHuman = humanizeIdentifier(username);
  if (usernameHuman) return usernameHuman;
  const contactHuman = humanizeIdentifier(fallbackAdminContact(user));
  if (contactHuman) return contactHuman;
  return '';
}

export function primaryAdminUserName(user) {
  return deriveAdminDisplayName(user);
}

export function adminUserSortKey(user) {
  const name = deriveAdminDisplayName(user);
  if (name) return name.toLowerCase();
  const contact = fallbackAdminContact(user).toLowerCase();
  if (contact) return contact;
  if (user && user.id != null) return `user-${user.id}`;
  return '';
}

export function contactIsRedundant(name, contact) {
  if (!contact) return true;
  if (!name) return false;
  const normalizedName = name.toLowerCase();
  const contactLower = contact.toLowerCase();
  if (contactLower === normalizedName) return true;
  const contactHuman = humanizeIdentifier(contact).toLowerCase();
  if (contactHuman && contactHuman === normalizedName) return true;
  return false;
}

export function formatAdminUserLabel(user) {
  if (!user) return '';
  const parts = [];
  const name = deriveAdminDisplayName(user);
  if (name) {
    parts.push(name);
  }
  const role = derivePrimaryAssignmentRole(user);
  if (role) {
    parts.push(role);
  }
  const contact = fallbackAdminContact(user);
  if (contact && !contactIsRedundant(name, contact)) {
    parts.push(contact);
  }
  if (user.is_active === false) {
    parts.push('inactive');
  }
  if (!parts.length) {
    if (user && user.id != null) return `User #${user.id}`;
    return '';
  }
  return parts.join(' • ');
}

export function formatAssignmentOptionLabel(user) {
  if (!user) return '';
  const parts = [];
  const name = deriveAdminDisplayName(user);
  const contact = fallbackAdminContact(user);
  if (name) {
    parts.push(name);
  } else if (user.id != null) {
    parts.push(`User #${user.id}`);
  }
  const role = derivePrimaryAssignmentRole(user);
  if (role) parts.push(role);
  if (contact && !contactIsRedundant(name, contact)) {
    parts.push(contact);
  }
  if (user.is_active === false) parts.push('inactive');
  return parts.join(' • ');
}


// ---- Board queue panel (per-assignee open tickets + oldest wait) -----------

function _queueEsc(s) {
  return String(s == null ? '' : s).replace(/[&<>"]/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]
  ));
}

/** Compact "time in queue" from an ISO timestamp: 45m / 6h / 3d. */
export function formatQueueAge(iso) {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  if (!(ms > 0)) return 'now';
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 48) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

function _queueAgeColor(iso) {
  if (!iso) return '#94a3b8';
  const hours = (Date.now() - new Date(iso).getTime()) / 3600000;
  if (hours >= 72) return '#C62828';   // 3+ days waiting
  if (hours >= 24) return '#E65100';   // 1+ day
  return '#15803d';
}

/**
 * Render a per-assignee queue panel: one row per staff member with their open
 * ticket count and how long the oldest has waited. `rows` are
 * {name, queue_count, oldest_created_at}, longest-waiting first.
 */
export function renderBoardQueuePanel(container, rows, opts = {}) {
  if (!container) return;
  const title = opts.title || 'Open queues';
  const icon = opts.icon || '📋';
  if (!rows || !rows.length) {
    container.innerHTML = `<div style="font-weight:600; font-size:12px; margin-bottom:6px;">${icon} ${_queueEsc(title)}</div>`
      + '<div class="muted" style="font-size:12px;">No open tickets in any queue. ✅</div>';
    return;
  }
  const totalTickets = rows.reduce((acc, r) => acc + (Number(r.queue_count) || 0), 0);
  // Rows flow into as many ~230px columns as the card is wide (1 on narrow
  // panels, 2–3 when there's room) so a long roster stays short.
  const body = rows.map((r) => {
    const age = formatQueueAge(r.oldest_created_at);
    const color = _queueAgeColor(r.oldest_created_at);
    return `<div style="display:flex; align-items:center; gap:8px; font-size:12px; padding:3px 0; border-bottom:1px dashed var(--border,#eee);">
      <span style="flex:1; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;" title="${_queueEsc(r.name)}">${_queueEsc(r.name)}</span>
      <span title="Open tickets in queue" style="flex:0 0 auto; font-weight:700;">${Number(r.queue_count) || 0}</span>
      <span title="Oldest ticket still waiting" style="flex:0 0 46px; text-align:right; color:${color};">${_queueEsc(age)}</span>
    </div>`;
  }).join('');
  container.innerHTML = `<div style="display:flex; align-items:baseline; gap:6px; margin-bottom:6px;">
      <span style="font-weight:600; font-size:12px;">${icon} ${_queueEsc(title)}</span>
      <span class="muted" style="font-size:11px;" title="Open tickets across all queues">${totalTickets} in ${rows.length}</span>
      <span class="muted" style="font-size:11px; margin-left:auto;">staff · queue · oldest wait</span>
    </div>
    <div style="display:grid; grid-template-columns:repeat(auto-fill, minmax(230px, 1fr)); gap:0 20px;">${body}</div>`;
}
